import { Container, Graphics } from 'pixi.js'
import { Camera } from '../game/Camera'
import { ParticleManager } from './ParticleManager'
import { NeonColors } from '../ui/neon-theme'
import { mountToAppShell } from '../ui/appShell'

interface ShockwaveRing {
  ring: Graphics
  elapsed: number
  duration: number
  maxRadius: number
}

/**
 * Orchestrates complex visual effects that span multiple systems:
 * - Map explosion (shockwave ring + flash + camera shake + all snakes explode)
 * - Kill notification (gold edge flash + "KILL!" text)
 *
 * Delegates to Camera (shake), ParticleManager (explosions), and DOM overlays (flashes).
 */
export class VFXOrchestrator {
  private camera: Camera
  private particleManager: ParticleManager
  private worldContainer: Container
  private shockwaveRings: ShockwaveRing[] = []
  private activeOverlays: HTMLDivElement[] = []

  constructor(camera: Camera, particleManager: ParticleManager, worldContainer: Container) {
    this.camera = camera
    this.particleManager = particleManager
    this.worldContainer = worldContainer
  }

  /**
   * Nuclear-level map explosion sequence:
   * 1. White flash overlay
   * 2. Camera shake (30px, 1.5s)
   * 3. Expanding shockwave ring (PixiJS Graphics)
   * 4. All snakes explode into particles
   * 5. Orange afterglow (200ms delayed)
   */
  triggerMapExplosion(
    centerX: number,
    centerZ: number,
    fieldRadius: number,
    snakePositions: Array<{ x: number; z: number }>,
  ): void {
    // 1. White flash overlay (fades over 500ms)
    this.flashOverlay('#FFFFFF', 'rgba(255,255,255,0.9)', 500, 'full')

    // 2. Camera shake: 30px intensity, 1.5s duration
    this.camera.shake(30, 1.5)

    // 3. Expanding shockwave ring
    const ring = new Graphics()
    ring.x = centerX
    ring.y = centerZ
    this.worldContainer.addChild(ring)
    this.shockwaveRings.push({
      ring,
      elapsed: 0,
      duration: 2.0,
      maxRadius: fieldRadius * 1.2,
    })

    // 4. All snakes explode into particles
    for (const pos of snakePositions) {
      this.particleManager.emit('death_explosion', pos.x, pos.z)
      this.particleManager.emit('death_sparks', pos.x, pos.z)
    }

    // 5. Orange afterglow (delayed 200ms, fades over 1s)
    setTimeout(() => {
      this.flashOverlay(
        NeonColors.primary,
        NeonColors.primaryGlow,
        1000,
        'full',
      )
    }, 200)
  }

  /**
   * Gold/neon screen-edge flash with "KILL!" text when player kills another snake.
   * Both elements auto-remove after animation completes.
   */
  flashKillNotification(): void {
    // Gold edge flash
    this.flashOverlay(
      NeonColors.gold,
      NeonColors.goldGlow,
      400,
      'edges',
    )

    // "KILL!" text overlay
    const text = document.createElement('div')
    text.textContent = 'KILL!'
    text.style.cssText = `
      position: absolute; top: 30%; left: 50%;
      transform: translate(-50%, -50%) scale(1.5);
      font-size: 2rem; font-weight: 900;
      pointer-events: none; z-index: 151;
      color: ${NeonColors.gold};
      text-shadow: 0 0 20px ${NeonColors.goldGlow}, 0 0 40px ${NeonColors.goldGlow};
      opacity: 1;
      transition: opacity 600ms ease-out, transform 600ms ease-out;
      font-family: 'Inter', sans-serif;
    `
    mountToAppShell(text)
    requestAnimationFrame(() => {
      text.style.opacity = '0'
      text.style.transform = 'translate(-50%, -50%) scale(2)'
    })
    setTimeout(() => text.remove(), 700)
  }

  /**
   * Update active shockwave rings. Call every frame.
   */
  update(dt: number): void {
    let i = 0
    while (i < this.shockwaveRings.length) {
      const sw = this.shockwaveRings[i]
      sw.elapsed += dt
      const progress = sw.elapsed / sw.duration

      if (progress >= 1) {
        sw.ring.parent?.removeChild(sw.ring)
        sw.ring.destroy()
        this.shockwaveRings.splice(i, 1)
        continue
      }

      const radius = progress * sw.maxRadius
      const alpha = 1 - progress
      const thickness = 20 * (1 - progress) + 2

      sw.ring.clear()
      sw.ring.circle(0, 0, radius)
      sw.ring.stroke({ width: thickness, color: 0xFFAA44, alpha })

      i++
    }
  }

  /**
   * Reusable DOM overlay for screen flashes.
   * @param color Background color (for 'full' style)
   * @param glowColor Glow color for box-shadow (for 'edges' style)
   * @param durationMs Fade-out duration in milliseconds
   * @param style 'full' = solid background, 'edges' = inset box-shadow only
   */
  private flashOverlay(
    color: string,
    glowColor: string,
    durationMs: number,
    style: 'full' | 'edges',
  ): void {
    const flash = document.createElement('div')
    flash.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none; z-index: 150;
      ${style === 'edges'
        ? `box-shadow: inset 0 0 60px ${glowColor}, inset 0 0 100px ${glowColor};`
        : `background: ${color};`}
      opacity: 1;
      transition: opacity ${durationMs}ms ease-out;
    `
    mountToAppShell(flash)
    this.activeOverlays.push(flash)

    requestAnimationFrame(() => {
      flash.style.opacity = '0'
    })

    setTimeout(() => {
      flash.remove()
      const idx = this.activeOverlays.indexOf(flash)
      if (idx >= 0) this.activeOverlays.splice(idx, 1)
    }, durationMs + 100)
  }

  /**
   * Cleanup all active DOM overlays and PixiJS graphics.
   */
  dispose(): void {
    for (const overlay of this.activeOverlays) {
      overlay.remove()
    }
    this.activeOverlays.length = 0

    for (const sw of this.shockwaveRings) {
      sw.ring.parent?.removeChild(sw.ring)
      sw.ring.destroy()
    }
    this.shockwaveRings.length = 0
  }
}
